import { useState, useEffect, useRef, ReactNode } from 'react'
import { Button } from '@/components/Button.tsx'
import { IconRotateClockwise, IconPlus, IconMinus, IconTrash } from '@tabler/icons-react'
import { AlbumFile } from '@/models/albumFile.ts'
import { CropData } from '@/types/photoCrop.ts'
import Cropper, { ReactCropperElement } from "react-cropper"
import "cropperjs/dist/cropper.css"

interface ImageCropCardProps {
  image: AlbumFile
  index: number
  cropData: CropData[string]
  aspectRatio: number
  onCropChange: (crop: { x: number; y: number; width: number; height: number }) => void
  onCountChange: (delta: number) => void
  onToggleRotation: () => void
  onRemove: () => void
  disableRotation?: boolean
}

interface ImageCropCardProps {
  image: AlbumFile
  index: number
  cropData: CropData[string]
  aspectRatio: number
  onCropChange: (crop: { x: number; y: number; width: number; height: number }) => void
  onCountChange: (delta: number) => void
  onToggleRotation: () => void
  onRemove: () => void
  isEditingExisting?: boolean // NEW: flag to distinguish between new images and existing ones
  disableRotation?: boolean
  customControls?: ReactNode
}

export function ImageCropCard({
  image,
  index,
  cropData,
  aspectRatio,
  onCropChange,
  onCountChange,
  onToggleRotation,
  onRemove,
  isEditingExisting = false,
  disableRotation = false,
  customControls,
}: ImageCropCardProps) {
  const cropperRef = useRef<ReactCropperElement>(null)
  const [currentCrop, setCurrentCrop] = useState({
    x: 25,
    y: 25,
    width: 50,
    height: 50
  })
  const prevAspectRatioRef = useRef(aspectRatio)

  // Convert pixel coordinates to percentages
  const convertToPercentage = (cropper: any) => {
    try {
      const cropBoxData = cropper.getCropBoxData()
      const canvasData = cropper.getCanvasData()
      const imageData = cropper.getImageData()

      // Check if data is valid
      if (!cropBoxData || !canvasData || !imageData || !imageData.naturalWidth || !imageData.naturalHeight) {
        return { x: 0, y: 0, width: 100, height: 100 }
      }

      // Calculate the scale between displayed image and natural image
      const scaleX = imageData.naturalWidth / imageData.width
      const scaleY = imageData.naturalHeight / imageData.height

      // Get crop box position relative to the canvas (displayed image)
      const cropX = (cropBoxData.left - canvasData.left) * scaleX
      const cropY = (cropBoxData.top - canvasData.top) * scaleY
      const cropWidth = cropBoxData.width * scaleX
      const cropHeight = cropBoxData.height * scaleY

      // Convert to percentages relative to natural image dimensions
      const x = (cropX / imageData.naturalWidth) * 100
      const y = (cropY / imageData.naturalHeight) * 100
      const width = (cropWidth / imageData.naturalWidth) * 100
      const height = (cropHeight / imageData.naturalHeight) * 100

      return {
        x: Math.max(0, Math.min(100, isNaN(x) ? 0 : x)),
        y: Math.max(0, Math.min(100, isNaN(y) ? 0 : y)),
        width: Math.max(0, Math.min(100, isNaN(width) ? 100 : width)),
        height: Math.max(0, Math.min(100, isNaN(height) ? 100 : height))
      }
    } catch (error) {
      console.warn('Error converting crop to percentage:', error)
      return { x: 0, y: 0, width: 100, height: 100 }
    }
  }

  const handleCropEnd = () => {
    const cropper = cropperRef.current?.cropper
    if (cropper) {
      const percentageCrop = convertToPercentage(cropper)
      setCurrentCrop(percentageCrop)
      onCropChange(percentageCrop)
    }
  }

  const handleCropMove = () => {
    const cropper = cropperRef.current?.cropper
    if (cropper) {
      const percentageCrop = convertToPercentage(cropper)
      setCurrentCrop(percentageCrop)
    }
  }

  // Set initial crop from loaded data
  useEffect(() => {
    console.log('initial crop', cropData)
    if (cropData?.crop) {
     console.log('setting! initial crop', cropData)
      setCurrentCrop(cropData.crop)
    }
  }, [cropData])

  // Update cropper when aspect ratio changes (rotation)
  useEffect(() => {
    const cropper = cropperRef.current?.cropper
    if (cropper) {
      const aspectRatioChanged = prevAspectRatioRef.current !== aspectRatio
      prevAspectRatioRef.current = aspectRatio

      cropper.setAspectRatio(aspectRatio)

      // Only update crop data if aspect ratio actually changed (user rotated)
      if (aspectRatioChanged) {
        setTimeout(() => {
          const percentageCrop = convertToPercentage(cropper)
          setCurrentCrop(percentageCrop)
          onCropChange(percentageCrop)
        }, 100)
      }
    }
  }, [aspectRatio])

  return (
    <div className="bg-base-200 p-6 rounded-lg">
      {/* Card header */}
      { !customControls && <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Bild {index + 1}</h3>
        <div className="flex items-center gap-2">
          {/* Count controls */}
          <div className="flex items-center gap-2 bg-base-100 rounded-lg p-1">
            <Button
              size="sm"
              onClick={() => onCountChange(-1)}
              disabled={cropData?.count <= 1}
            >
              <IconMinus size={16} />
            </Button>
            <span className="px-3 font-medium">{cropData?.count || 1}×</span>
            <Button
              size="sm"
              onClick={() => onCountChange(1)}
            >
              <IconPlus size={16} />
            </Button>
          </div>

          {/* Rotation button */}
          {!disableRotation && (
            <Button
              size="sm"
              variant="outline"
              onClick={onToggleRotation}
            >
              <IconRotateClockwise size={16} />
            </Button>
          )}

          {/* Delete button */}
          <Button
            size="sm"
            color="error"
            variant="outline"
            onClick={onRemove}
          >
            <IconTrash size={16} />
          </Button>
        </div>
      </div> }
      { customControls }

      {/* Crop area */}
      <div className="flex justify-center mb-4">
        <div className="max-w-2xl w-full">
          <Cropper
            ref={cropperRef}
            src={image.detail || image.file}
            style={{ height: 'auto', width: '100%' }}
            aspectRatio={aspectRatio}
            guides={true}
            center={true}
            highlight={true}
            background={true}
            autoCropArea={1.0}
            checkOrientation={false}
            responsive={true}
            restore={false}
            modal={true}
            zoomable={false}
            scalable={false}
            viewMode={1}
            dragMode="crop"
            checkCrossOrigin={false}
            cropend={handleCropEnd}
            cropmove={handleCropMove}
            ready={() => {
              if (isEditingExisting) {
                // BASKET EDIT MODE: Set initial crop when ready
                const cropper = cropperRef.current?.cropper
                if (cropper && cropData?.crop) {
                  // Convert percentage crop to pixel coordinates for cropper
                  const imageData = cropper.getImageData()
                  const canvasData = cropper.getCanvasData()

                  if (imageData && canvasData) {
                    const scaleX = imageData.width / imageData.naturalWidth
                    const scaleY = imageData.height / imageData.naturalHeight

                    const cropX = (cropData.crop.x / 100) * imageData.naturalWidth * scaleX
                    const cropY = (cropData.crop.y / 100) * imageData.naturalHeight * scaleY
                    const cropWidth = (cropData.crop.width / 100) * imageData.naturalWidth * scaleX
                    const cropHeight = (cropData.crop.height / 100) * imageData.naturalHeight * scaleY

                    cropper.setCropBoxData({
                      left: Math.floor(canvasData.left + cropX),
                      top: Math.floor(canvasData.top + cropY),
                      width: Math.floor(cropWidth),
                      height: Math.floor(cropHeight)
                    })
                  }
                }

                // Update current crop display
                if (cropper) {
                  const percentageCrop = convertToPercentage(cropper)
                  setCurrentCrop(percentageCrop)
                }
              } else {
                // PRODUCT MODE: Debug: Check the actual src being used
                console.log('Cropper ready with src:', image.detail || image.file)

                // Initialize crop when ready
                const cropper = cropperRef.current?.cropper
                if (cropper) {
                  const percentageCrop = convertToPercentage(cropper)
                  setCurrentCrop(percentageCrop)
                  onCropChange(percentageCrop)
                }
              }
            }}
          />
        </div>
      </div>

      {/* Crop info */}
      <div className="text-center text-sm opacity-75">
        Position: {Math.round(currentCrop.x)}%, {Math.round(currentCrop.y)}% |
        Größe: {Math.round(currentCrop.width)}% × {Math.round(currentCrop.height)}% |
        Seitenverhältnis: {aspectRatio.toFixed(2)} {cropData?.rotated ? '(gedreht)' : ''}
      </div>
    </div>
  )
}
